/* eslint-disable no-restricted-imports, import/order */
import * as z from 'zod'
export * from 'zod'
// import 'zod-openapi/extend'
import { isVAT } from 'validator'
/* eslint-enable no-restricted-imports, import/order*/

import { ObjectId } from '@/utils/objectid'
import { isBic } from '@/utils/misc/isBic'

//
// Unique array (simple types only otherwise uses .toString())
//

type UniqueArrayProps<T extends z.ZodType> = {
    min?: number
    max?: number
    extend?: (type: z.ZodArray<T>) => z.ZodArray<T>
}

export const uniqueArray = <T extends z.ZodType>(schema: T, { min, max, extend }: UniqueArrayProps<T> = {}) => {
    let arrayType = z.array(schema)

    if (min) {
        arrayType = arrayType.min(min)
    }

    if (max) {
        arrayType = arrayType.max(max)
    }

    if (extend) {
        arrayType = extend(arrayType)
    }

    return arrayType.refine((items) => new Set(items.map((x) => (x as any).toString())).size === items.length, {
        message: 'List contains duplicated items',
    })
}

//
// MongoDB ObjectId validator
//

export const objectIdString = (zodString?: z.ZodString) =>
    (zodString || z.string())
        .transform((value, ctx) => {
            try {
                return new ObjectId(value) as ObjectId
            } catch (error) {
                ctx.issues.push({
                    code: 'custom',
                    input: value,
                    params: {
                        i18n: { key: 'Not a valid ObjectId' },
                    },
                })
                return z.NEVER
            }
        })
        .meta({ override: { type: 'string' } })

export const objectId = () =>
    z
        .instanceof(ObjectId)
        .meta({
            // Tell zod-openapi how to represent this in the OpenAPI spec
            effectType: 'output',
            type: 'string',
            format: 'ObjectId',
            description: 'nanoid ObjectId as string',
        })
        .or(objectIdString())
        .meta({
            // Tell zod-openapi how to represent this in the OpenAPI spec
            effectType: 'output',
            type: 'string',
            format: 'ObjectId',
            description: 'nanoid ObjectId as string',
        })

//
// RRule validator
//

export const rrule = (mustEnd: boolean, zodString?: z.ZodString) =>
    (zodString || z.string()).check(async ({ value, ...ctx }) => {
        try {
            const RRule = (await import('rrule')).RRule

            const rule = RRule.fromString(value)

            if (mustEnd) {
                if (!rule.origOptions.count && !rule.origOptions.until) {
                    ctx.issues.push({
                        code: 'custom',
                        input: value,
                        params: {
                            i18n: { key: 'custom.rruleMustEnd' },
                        },
                    })
                }
            }
        } catch {
            ctx.issues.push({
                code: 'custom',
                input: value,
                params: {
                    i18n: { key: 'custom.invalidRRule' },
                },
            })
        }
    })

//
// Phone validator and transformer, ensures final format is in E.164
//

export const phone = (zodString?: z.ZodString) =>
    (zodString || z.string())
        .transform(async (value: string, ctx) => {
            try {
                const { formatPhone } = await import('@/utils/misc/phone')
                return formatPhone(value, 'E.164')
            } catch (e) {
                ctx.issues.push({
                    code: 'custom',
                    input: value,
                    params: {
                        i18n: { key: 'custom.invalidPhone' },
                    },
                })

                return z.NEVER
            }
        })
        .meta({ override: { type: 'string', format: 'phone' } })

// TODO : Currently this is an alias to generic phone due trying to avoid
// using the big data library from libphone so we currently don't really know if
// the given phone is a valid mobile or just a regular phone
export const mobilePhone = phone

//
// Tax-ID validator. Because we require the country we expect tax-ids
// to be prefixed by the the country like DE_36574261809 however we'll
// normalize that and will return the tax id without the country specifier then.
//

export const taxID = (zodString?: z.ZodString) =>
    (zodString || z.string()).transform((value: string, ctx) => {
        const country = (value || '').substring(0, 2)
        const taxID = (value || '').substring(2).replace(/\D/g, '')

        if (country && taxID) {
            //
            // This is ugly as fuck we need to fix this one day
            //

            let isValid = false

            switch (country) {
                case 'AT':
                    // TODO : This is not a real validation
                    isValid = taxID.length === 9 && /\d+/.test(taxID)
                    break
                case 'DE':
                    // TODO : This is not a real validation
                    isValid = [10, 11].includes(taxID.length) && /\d+/.test(taxID)
                    break
                default:
                    throw new Error(`Unsupported country "${country}" for isTaxID`)
            }

            if (isValid) {
                return taxID
            }
        }

        ctx.issues.push({
            code: 'custom',
            input: value,
            params: {
                i18n: { key: 'custom.invalidTaxID' },
            },
        })

        return z.NEVER
    })

//
// VAT validator
//

export const VATID = (zodString?: z.ZodString) =>
    (zodString || z.string()).check(({ value, ...ctx }) => {
        if (value) {
            const country = value.substring(0, 2)

            try {
                if (isVAT(value, country)) {
                    return undefined // ok
                }
            } catch {
                // noop
            }
        }

        ctx.issues.push({
            code: 'custom',
            input: value,
            params: {
                i18n: { key: 'custom.invalidVATID' },
            },
        })
    })

//
// Creditor Identifier validator
//

const CREDITOR_IDENTIFIER_REGEX =
    // eslint-disable-next-line no-useless-escape, max-len
    /[a-zA-Z]{2,2}[0-9]{2,2}([A-Za-z0-9]|[\+|\?|\/|\-|\:|\(|\)|\.|,|']){3,3}([A-Za-z0-9]|[\+|\?|\/|\-|:|\(|\)|\.|,|']){1,28}/

const CREDITOR_IDENTIFIER_DIGITS: Record<string, number> = {
    AT: 16,
    DE: 16,
    // TODO : Extend for other countries
}

export const creditorIdentifier = (zodString?: z.ZodString) =>
    (zodString || z.string()).check(({ value, ...ctx }) => {
        const match = value.match(CREDITOR_IDENTIFIER_REGEX)

        if (match) {
            const digits = CREDITOR_IDENTIFIER_DIGITS[value.substring(0, 2)] || undefined
            if (digits === undefined || value.length === digits + 2) {
                return undefined // ok
            }
        }

        ctx.issues.push({
            code: 'custom',
            input: value,
            params: {
                i18n: { key: 'custom.invalidCreditorIdentifier' },
            },
        })
    })

//
// BIC validator
//

export const bic = (zodString?: z.ZodString) =>
    (zodString || z.string()).check(({ value, ...ctx }) => {
        if (!isBic(value)) {
            ctx.issues.push({
                code: 'custom',
                input: value,
                params: {
                    i18n: { key: 'custom.invalidBIC' },
                },
            })
        }
    })

//
// IBAN validator
//

export const iban = (zodString?: z.ZodString) =>
    (zodString || z.string())
        .transform((value) => value.replace(/\s/g, ''))
        .check(async ({ value, ...ctx }) => {
            const { isIBAN } = await import('@/utils/misc/isIBAN')

            if (!isIBAN(value)) {
                // if failed, it means the provided iban is not valid
                ctx.issues.push({
                    code: 'custom',
                    input: value,
                    params: {
                        i18n: { key: 'custom.invalidIBAN' },
                    },
                })
            }
        })
        .meta({ override: { type: 'string', format: 'iban' } })

//
// JSON
//

const JSONLiteralSchema = z.union([z.string(), z.number(), z.boolean(), z.null()])

type JSONLiteral = z.infer<typeof JSONLiteralSchema>

export type JSON = JSONLiteral | { [key: string]: JSON } | JSON[]

export const JSONSchema: z.ZodType<JSON> = z.lazy(() =>
    z.union([JSONLiteralSchema, z.array(JSONSchema), z.record(z.string(), JSONSchema)])
)
// .openapi({ ref: 'JSON', title: 'JSON' })

export const json = () => JSONSchema

export const jsonString = (zodString?: z.ZodString) =>
    (zodString || z.string()).transform((str, ctx): JSON => {
        try {
            return JSON.parse(str)
        } catch (e) {
            ctx.issues.push({ code: 'custom', message: 'Invalid JSON', input: str })
            return z.NEVER
        }
    })

//
// Date
//

export const isoDateTime = z.preprocess(
    (arg: Date) => {
        // fail straight away if null date intead of giving epoch date 1970-01-01T00:00:00.000Z
        // @ts-expect-error its fine
        if (arg === null || arg === 0) {
            return undefined
        }
        return arg
    },
    z.coerce
        .date()
        .transform((date) => {
            // Set milliseconds to 000
            date.setUTCMilliseconds(0)
            return date
        })
        .meta({ override: { type: 'date', format: 'date-time' } })
)

export const isoDateOnly = z.preprocess(
    (arg) => {
        // fail straight away if null date intead of giving epoch date 1970-01-01T00:00:00.000Z
        if (arg === null || arg === 0) {
            return undefined
        }
        return arg
    },
    z.coerce.date().check(({ value, issues }) => {
        const [h, m, s, ms] = [
            value.getUTCHours(),
            value.getUTCMinutes(),
            value.getUTCSeconds(),
            value.getUTCMilliseconds(),
        ]

        if (h !== 0 || m !== 0 || s !== 0 || ms !== 0) {
            issues.push({
                code: 'custom',
                input: value,
                params: {
                    i18n: { key: 'custom.invalidDateOnly' },
                },
            })
        }
    })
)

//
// Hex-Color
//

// We don't use validator/isHexColor because we only want to support 6-digits regular hex colors
// as thats most compatible with all formats

const HEX_COLOR_REGEX = /^#[0-9A-F]{6}$/i

export const hexColor = (zodString?: z.ZodString) => (zodString || z.string()).regex(HEX_COLOR_REGEX)

//
// Hex-Color with (optional) alpha channel
//

const HEX_COLOR_ALPHA_REGEX = /^#[0-9A-F]{6,8}$/i

export const hexColorAlpha = (zodString?: z.ZodString) => (zodString || z.string()).regex(HEX_COLOR_ALPHA_REGEX)

export const getZodKeys = (schema: z.core.$ZodType): string[] => {
    if (schema === null || schema === undefined) {
        return []
    }

    if (schema instanceof z.ZodObject) {
        return Object.keys(schema.shape)
    }

    if (schema instanceof z.ZodPipe) {
        return getZodKeys(schema.def.in)
    }

    if (schema instanceof z.ZodNullable || schema instanceof z.ZodOptional || schema instanceof z.ZodDefault) {
        return getZodKeys(schema.unwrap())
    }

    if (schema instanceof z.ZodArray) {
        return getZodKeys(schema.element)
    }

    return []
}

export const recordWithRequiredKeys = <K extends Record<string, any>, V extends z.ZodType>(
    keysSchema: z.ZodEnum<K>,
    valueSchema: V
) =>
    z
        .record(keysSchema, valueSchema)
        // this makes the validation
        .check((ctx) => {
            keysSchema.options.forEach((key) => {
                const result = valueSchema.safeParse(ctx.value[key])
                if (!result.success) {
                    result.error.issues.forEach((error) =>
                        ctx.issues.push({
                            ...error,
                            path: [key, ...error.path],
                        })
                    )
                }
            })
        })
// this is just to infer the right type
// .refine((val): val is Record<K, z.infer<V>> => true)

// Misc schemas
export const singleLineString = (zodString?: z.ZodString) =>
    (zodString || z.string()).transform((value) => value.replace(/(?:\r\n|\r|\n)/g, ' '))

// We needed to add a customized email validation since zod currently does not accept umlauts
// https://github.com/colinhacks/zod/pull/3678
export const emailRegexUtf8Extended =
    /^(?!.*[@]{2})(?!.*[.]{2})(?!.*[.@]$)[\p{L}\p{N}!#$%&'*+/=?^_{|}~-](?:[\p{L}\p{N}!#$%&'*+/=?^_{|}~.-]*[\p{L}\p{N}!#$%&'*+/=?^_{|}~-])?@[\p{L}\p{N}][\p{L}\p{N}-]*?(\.[\p{L}\p{N}-]+)*\.[\p{L}]{2,}$/u

export const email = (zodString?: z.ZodString) =>
    (zodString || z.string()).check(({ value, ...ctx }) => {
        if (!emailRegexUtf8Extended.test(value)) {
            ctx.issues.push({
                code: 'custom',
                input: value,
                params: {
                    i18n: { key: 'custom.invalidEmail' },
                },
            })
        }
    })

//
// Domain name
//

const DOMAIN_NAME_REGEX = /^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z]{2,})+$/

export const domainName = (zodString?: z.ZodString) => (zodString || z.string()).regex(DOMAIN_NAME_REGEX)

//
// merge function that handle up to 3 merges at once
//

export function merge<S1 extends z.ZodRawShape, S2 extends z.ZodRawShape>(o1: z.ZodObject<S1>, o2: z.ZodObject<S2>) {
    const mergedShape: S1 & S2 = {
        ...o1.shape,
        ...o2.shape,
    }

    return z.object(mergedShape).strict()
}

export function merge3<S1 extends z.ZodRawShape, S2 extends z.ZodRawShape, S3 extends z.ZodRawShape>(
    o1: z.ZodObject<S1>,
    o2: z.ZodObject<S2>,
    o3: z.ZodObject<S3>
) {
    const mergedShape: S1 & S2 & S3 = {
        ...o1.shape,
        ...o2.shape,
        ...o3.shape,
    }

    return z.object(mergedShape).strict()
}
export const regex = (flags?: string) =>
    z
        .string()
        .min(1)
        .refine(
            (val) => {
                try {
                    new RegExp(val, flags)
                } catch (error) {
                    if (error instanceof SyntaxError) {
                        return false
                    }
                }

                return true
            },
            {
                params: {
                    i18n: { key: 'custom.invalidRegex' },
                },
            }
        )

//
// A helper to create toggleable schemas where if enabled is false the rest of the schema is not required and ignored
//

export const toggleable = <T extends z.ZodRawShape>(shape: T) =>
    z.discriminatedUnion('enabled', [
        z.object({ enabled: z.literal(false) }),
        z.object({ enabled: z.literal(true), ...shape }),
    ])

/**
 * this is a hacky function to simplify the type inference of zod schemas
 */
export const toZodType = <Schema extends z.ZodType<any, any>>(schema: Schema) => schema as z.ZodType<z.infer<Schema>>
